import React, { useEffect, useMemo, useRef } from "react";
import { CheckoutAccordion } from "@shopflo/ui/accordian/CheckoutAccordian";
import useCartNotes from "lib/hooks/useCartNotes";
import { useLocale } from "lib/hooks/useLocale";
import { CartNoteIcon } from "lib/types/cartNotes";
import { currencyFormatter } from "lib/utils/formatters";
import { classNames, truncateString } from "lib/utils/helpers";
import { NotePencil, Gift, Trash, WarningCircle, Note, Plus, IconWeight } from "@phosphor-icons/react";
import { errorToast } from "lib/utils/toasters";
import { Icon } from "@shopflo/ui/icon";
import { BaseTextArea } from "@shopflo/ui/base-text-area";
import { NotesConfigType } from "lib/types/merchantConfigTypes";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { hexToRgba, getTextColorByBrightness, getStyleFromCSSVariable } from "@shopflo/ui/utils";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";

const CartNotes = ({
  customisation
}: {
  customisation?: NotesConfigType;
}) => {
  const cartNoteRef = useRef<any>(null);
  const { t } = useLocale();

  const getCartNoteIcon = (icon: CartNoteIcon, {
    active,
    mode = "regular"
  }: {
    active: boolean;
    mode?: IconWeight;
  }) => {
    let IconComponent = null;
    switch (icon) {
      case "ATTACH":
        IconComponent = Note;
        break;
      case "EDIT":
        IconComponent = NotePencil;
        break;
      case "GIFT":
        IconComponent = Gift;
        break;
      default:
        return null;
    }
    return (
      <Icon
        IconComponent={IconComponent}
        iconProps={{
          weight: mode
        }}
        size="sm"
        style={{
          color: active ? hexToRgba(customisation?.textColor ?? '', 0.6) : customisation?.textColor,
        }}
      />
    );
  };
  const {
    state: { cartNoteAttributes, appliedCartNote, isCartNoteTileLoading, isEditMode, inputValue, showError },
    actions: { handleSaveNote, setIsEditMode, setInputValue, setShowError },
  } = useCartNotes();

  useEffect(() => {
    if (isEditMode) {
      cartNoteRef?.current?.openAccordion();
    }
  }, [isEditMode]);

  const handleCancel = () => {
    if (isEditMode) {
      setIsEditMode(false);
      setInputValue(appliedCartNote.note);
    } else {
      setInputValue("");
    }
    setShowError(false)
    cartNoteRef?.current?.closeAccordion();
  };

  const handleSave = async (action: "ADD" | "EDIT" | "DELETE" = "ADD") => {
    if (!inputValue.trim()) {
      setShowError(true);
      return;
    }
    try {
      handleSaveNote({ inputValue, action });
    } catch (error) {
      console.error("Error saving note");
      errorToast(t("error_saving_note"));
    }
  };

  const handleEdit = () => setIsEditMode(true);

  const handleDelete = () => handleSaveNote({ inputValue: "", action: "DELETE" });

  const showCartNoteTile = useMemo(() => {
    if (isEditMode) {
      return true;
    } else if (!appliedCartNote.hasCartNoteAdded) {
      return true;
    }
    return false;
  }, [appliedCartNote.hasCartNoteAdded, isEditMode]);

  const primaryButtonStyles = useMemo(() => ({
    ...getStyleFromCSSVariable('--flo-primary-button-color', 'backgroundColor'),
    ...getStyleFromCSSVariable('--flo-primary-button-text-color', 'color'),
  }), []);

  if (!cartNoteAttributes.hasCartNote) {
    return <></>;
  }

  const renderAppliedCartNote = () => {
    return (
      <div
        className="flex flex-col gap-2 p-3 rounded-xl"
        onClick={handleEdit}
        style={{
          backgroundColor: customisation?.backgroundColor,
          borderRadius: customisation?.cardRadius,
          ...getCardBorderStyles({
            borderWidth: customisation?.cardBorderWidth,
            borderSides: customisation?.cardBorderSides,
            borderColor: customisation?.borderColor,
          }),
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            {getCartNoteIcon(cartNoteAttributes.cartNoteConfig.icon, { active: true, mode: "fill" })}
            <p
              className="text-sm text-ellipsis"
              style={{
                color: customisation?.textColor,
              }}
            >
              {truncateString(appliedCartNote.note, 50)}
            </p>
          </div>
          <div className="flex">
            <button
              onClick={handleDelete}
              className="text-sm flex items-center gap-1">
              <Icon IconComponent={Trash} size="sm" iconProps={{ color: customisation?.textColor }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const cartNoteSkeleton = () => {
    return (
      <div className="h-[70px] w-full px-2 py-3 space-y-2 bg-transparent"
        style={{
          borderColor: getTextColorByBrightness({ bgHexColor: "#71717A", lightOpacity: 24 }),
        }}
      >
        <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
        <div className="skeleton-loader h-[14px] rounded-md w-full"></div>
      </div>
    );
  };

  const renderCartNoteTile = () => {
    return (
      <CheckoutAccordion
        id={"cart_notes"}
        ref={cartNoteRef}
        defaultOpen={false}
        renderTitleIcon={(active) => getCartNoteIcon(cartNoteAttributes.cartNoteConfig.icon, { active: active }) ?? <div></div>}
        renderTitleText={(active) => (
          <div className="text-base flex gap-2 items-center"
            style={{
              color: customisation?.textColor,
            }}
          >
            <h3 className={classNames("text-zinc-500", active ? "text-text-xs-medium text-opacity-60" : "text-text-sm-medium")}
              style={{
                color: active ? hexToRgba(customisation?.textColor ?? '', 0.6) : customisation?.textColor ?? '',
              }}
            >
              {cartNoteAttributes.cartNoteConfig.title}
            </h3>
            {cartNoteAttributes.type === "PAID" && (
              <p className={classNames("text-zinc-500", active ? "text-text-xs-medium text-opacity-60" : "text-text-sm-medium")}
                style={{
                  color: customisation?.textColor ?? '',
                }}
              >
                ({currencyFormatter(cartNoteAttributes?.cartNoteAddOnData?.price?.toString())})
              </p>
            )}
          </div>
        )}
        callbackOnToggle={(isOpen: boolean) => {
          setIsEditMode(false);
        }}
        buttonClass="!h-auto !py-3 !rounded-lg !bg-transparent"
        customClass="!mx-0 !mt-0 !bg-transparent !rounded-lg"
        renderAccordionIcon={(active) =>
          active ? null : <Icon
            IconComponent={Plus}
            size="sm"
            iconProps={{
              weight: "bold",
              color: customisation?.textColor ?? ''
            }} />
        }
        content={
          <div className="flex flex-col px-3 pb-3 gap-3">
            <div
              className="flex w-full flex-col gap-2"
              style={
                {
                  ['--placeholder-color' as string]: hexToRgba(customisation?.textColor ?? '', 0.8),
                  ['--text-color' as string]: customisation?.textColor,
                } as React.CSSProperties
              }
            >
              <BaseTextArea
                variant="ghost"
                dimension="xs"
                value={inputValue}
                onChange={(e) => {
                  setShowError(false);
                  setInputValue(e.target.value);
                }}
                error={showError}
                placeholder={cartNoteAttributes.cartNoteConfig.suggestionText}
                textAreaClassName="!bg-transparent [&::placeholder]:![color:var(--placeholder-color)] ![color:var(--text-color)]"
              />
              {showError && (
                <p className="text-text-xs-medium text-ouch flex flex-row items-center gap-1">
                  <WarningCircle size={14} />
                  {t("cart_note_error")}
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                style={{
                  borderColor: getTextColorByBrightness({ bgHexColor: customisation?.backgroundColor ?? '', lightOpacity: 24 }),
                  color: customisation?.textColor ?? '',
                }}
                className="px-4 py-1 rounded-lg text-text-sm-semibold border text-zinc-600"
                onClick={handleCancel}
              >
                {t("cancel")}
              </button>
              <button
                className="px-4 py-1 rounded-lg text-text-sm-semibold text-white bg-primary-dark"
                style={primaryButtonStyles}
                onClick={() => handleSave("ADD")}
              >
                {t("save")}
              </button>
            </div>
          </div>
        }
      />
    );
  };

  return (
    <div>
      {appliedCartNote.hasCartNoteAdded && !isEditMode && !isCartNoteTileLoading && renderAppliedCartNote()}
      {isCartNoteTileLoading &&
      <>
          {cartNoteSkeleton()}
          <OverlaySpinner/>
      </> 
      }
      {showCartNoteTile && !isCartNoteTileLoading &&
        <div className="rounded-xl overflow-hidden"
          style={{
            borderRadius: customisation?.cardRadius,
            backgroundColor: customisation?.backgroundColor,
            ...getCardBorderStyles({
              borderWidth: customisation?.cardBorderWidth,
              borderSides: customisation?.cardBorderSides,
              borderColor: customisation?.borderColor,
            }),

          }}
        >
          {renderCartNoteTile()}
        </div>}
    </div>
  );
};

export default CartNotes;
